import { FC, ReactNode } from 'react';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

interface DashboardWidgetProps {
  icon: ReactNode;
  iconColor?: string;
  title: string;
  subtitle?: string;
  right?: ReactNode;
  children: ReactNode;
  /** Optional background (the AI widget uses a subtle gradient). */
  background?: string;
}

/**
 * Card shell shared by every dashboard widget: a header row (icon tile, title,
 * subtitle, optional right-aligned action) above the widget body. Uses theme
 * tokens + `sx`, so it flips cleanly under RTL.
 */
export const DashboardWidget: FC<DashboardWidgetProps> = observer(
  ({ icon, iconColor, title, subtitle, right, children, background }) => {
    const theme = useTheme();
    return (
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: 4,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          background: background ?? theme.palette.common.white,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
            flexShrink: 0,
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2,
              backgroundColor: theme.palette.grey[100],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: iconColor ?? theme.palette.text.secondary,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 120 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'text.primary' }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography sx={{ fontSize: 12, color: 'text.tertiary', mt: 0.25 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {right}
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {children}
        </Box>
      </Paper>
    );
  },
);
